import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../../../services/auth.service';
import { InteractionsService } from '../../../../services/interactions.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChangePasswordDialog } from '../change-password-dialog/change-password-dialog';

@Component({
  selector: 'app-profile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatChipsModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatDialogModule
],
  templateUrl: './profile.html',
  styleUrls: ['./profile.scss']
})
export class Profile implements OnInit, OnDestroy {
  currentUser: any = null;
  private userSubscription: Subscription = new Subscription();
  lastActivity: string | null = null;
  
  // Szerkeszthető felhasználónév tulajdonságok
  editableUsername: string = '';
  displayUsername: string = '';
  isEditingUsername: boolean = false;
  private isInitialized: boolean = false;

  constructor(
    private authService: AuthService,
    private interactionsService: InteractionsService,
    private router: Router,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.userSubscription = this.authService.userData$.subscribe(userData => {
      this.currentUser = userData;
      if (userData && !this.isInitialized) {
        // Inicializáljuk a szerkeszthető felhasználónevet csak egyszer
        this.editableUsername = userData.username || userData.name || '';
        this.displayUsername = this.editableUsername;
        this.isInitialized = true;
        this.loadLastActivity();
        this.cdr.markForCheck();
      } else if (userData && this.isInitialized) {
        // Ha már inicializált, csak a displayUsername-et frissítjük
        this.displayUsername = userData.username || userData.name || 'Nincs megadva';
        this.cdr.markForCheck();
      } else if (!userData) {
        // Ha nincs bejelentkezett felhasználó, irányítsuk át a login oldalra
        this.router.navigate(['/login']);
      }
    });
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  getUserIcon(): string {
    if (!this.currentUser?.jobs || this.currentUser.jobs.length === 0) {
      return 'person';
    }
    
    const roles = this.currentUser.jobs;
    
    if (roles.includes('admin') || roles.includes('sales_manager') || roles.includes('deputy_ceo')) {
      return 'account_circle';
    }
    
    if (roles.includes('call_center')) {
      return 'support_agent';
    }
    
    if (roles.includes('kam')) {
      return 'person_pin';
    }
    
    if (roles.includes('investment')) {
      return 'account_balance';
    }
    
    return 'person';
  }

  getDisplayUsername(): string {
    return this.displayUsername || 'Nincs megadva';
  }

  getFormattedCreatedDate(): string {
      try {
      const date = new Date(this.currentUser.created_at);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch (error) {
      return '-'; // Fallback érték hiba esetén
    }
  }

  getUserStatus(): string {
    if (this.currentUser?.is_active === true) {
      return 'Aktív';
    } else if (this.currentUser?.is_active === false) {
      return 'Inaktív';
    } else {
      return 'Aktív'; // Fallback érték, ha nincs beállítva
    }
  }

  loadLastActivity(): void {
    if (!this.currentUser?.id) {
      return;
    }

    // A felhasználó legutolsó aktivitását lekérdezzük az interactions API-ból
    const userId = this.currentUser.id;
    this.interactionsService.getUserLastActivity(userId).subscribe({
      next: (response: any) => {
        if (response.success && response.timestamp) {
          this.lastActivity = response.timestamp;
        }
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Hiba a legutolsó aktivitás betöltésekor:', error);
        this.cdr.markForCheck();
      }
    });
  }

  getFormattedLastActivity(): string {
    if (!this.lastActivity) {
      return 'Nincs adat';
    }
    
    try {
      const date = new Date(this.lastActivity);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}.${month}.${day} ${hours}:${minutes}`;
    } catch (error) {
      return 'Nincs adat';
    }
  }

  onChangePassword(): void {
    const dialogRef = this.dialog.open(ChangePasswordDialog, {
      width: '500px',
      disableClose: true,
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // A felhasználó megadta az új jelszót
        this.authService.changePassword(result.currentPassword, result.newPassword).subscribe({
          next: (response) => {
            if (response.success) {
              this.snackBar.open('Jelszó sikeresen megváltoztatva!', 'Rendben', {
                duration: 5000,
                panelClass: ['success-snackbar']
              });
            } else {
              this.snackBar.open(response.message || 'Hiba történt a jelszó változtatása során!', 'Rendben', {
                duration: 5000,
                panelClass: ['error-snackbar']
              });
            }
          },
          error: (err) => {
            const errorMessage = err.error?.detail || 'Hiba történt a jelszó változtatása során!';
            this.snackBar.open(errorMessage, 'Rendben', {
              duration: 5000,
              panelClass: ['error-snackbar']
            });
          }
        });
      }
    });
  }

  onBack(): void {
    this.router.navigate(['/overview']);
  }

  // Felhasználónév szerkesztési metódusok
  toggleUsernameEdit(): void {
    this.isEditingUsername = !this.isEditingUsername;
    if (this.isEditingUsername) {
      // Szerkesztési mód kezdése - inicializáljuk a szerkeszthető értéket
      this.editableUsername = this.currentUser?.username || this.currentUser?.name || '';
      // Fokuszáljunk az input mezőre egy kis késleltetés után
      setTimeout(() => {
        const inputElement = document.querySelector('.editable-field input') as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
          inputElement.select();
        }
      }, 100);
    } else {
      // Szerkesztés befejezése - visszaállítjuk az eredeti értéket
      this.editableUsername = this.currentUser?.username || this.currentUser?.name || '';
    }
  }

  onUsernameBlur(): void {
    // Amikor a mező elveszti a fókuszt, befejezzük a szerkesztést
    if (this.isEditingUsername) {
      this.saveUsernameEdit();
    }
  }

  onUsernameKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.saveUsernameEdit();
    } else if (event.key === 'Escape') {
      this.cancelUsernameEdit();
    }
  }

  saveUsernameEdit(): void {
    if (this.isEditingUsername) {
      // Elmentjük a jelenlegi és az új értéket
      const newUsername = this.editableUsername;
      const oldUsername = this.currentUser?.username || this.currentUser?.name || '';
      
      // Ellenőrizzük, hogy nem üres-e
      if (!newUsername || newUsername.trim() === '') {
        this.snackBar.open('A felhasználónév nem lehet üres!', 'Rendben', { 
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        return;
      }

      // Ha nincs változás, ne csináljunk semmit
      if (newUsername === oldUsername) {
        this.isEditingUsername = false;
        return;
      }
      
      // Visszaállítjuk a szerkesztési állapotot
      this.isEditingUsername = false;
      
      // API hívás a felhasználónév mentéséhez
      this.authService.updateUsername(newUsername).subscribe({
        next: (result) => {
          if (result.success) {
            // Sikeres frissítés - rögzítjük az interactions táblába
            this.logUsernameChange(oldUsername, newUsername);
            
            // Sikeres frissítés
            this.snackBar.open('Felhasználónév sikeresen frissítve!', 'Rendben', { 
              duration: 3000,
              panelClass: ['success-snackbar']
            });
            // Frissítjük a displayUsername-et is
            this.displayUsername = newUsername;
            this.cdr.markForCheck();
          } else {
            // Hiba történt, visszaállítjuk az eredeti értéket és megjelenítjük a hibaüzenetet
            this.snackBar.open(result.message || 'Hiba a felhasználónév frissítése során!', 'Rendben', { 
              duration: 5000,
              panelClass: ['error-snackbar']
            });
            // Visszaállítjuk az eredeti értéket
            this.editableUsername = this.currentUser?.username || this.currentUser?.name || '';
            this.displayUsername = this.editableUsername;
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          const errorMessage = err.error?.detail || 'Hiba a felhasználónév frissítése során!';
          this.snackBar.open(errorMessage, 'Rendben', { 
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          // Visszaállítjuk az eredeti értéket
          this.editableUsername = this.currentUser?.username || this.currentUser?.name || '';
          this.displayUsername = this.editableUsername;
          this.cdr.markForCheck();
        }
      });
    }
  }

  cancelUsernameEdit(): void {
    this.isEditingUsername = false;
    // Visszaállítjuk az eredeti értéket
    this.editableUsername = this.currentUser?.username || this.currentUser?.name || '';
    this.displayUsername = this.editableUsername;
  }

  private logUsernameChange(oldUsername: string, newUsername: string): void {
    if (!this.currentUser?.id) {
      return;
    }

    // Interactions bejegyzés készítése
    const interactionData = {
      customerid: null,
      companyid: null,
      notes: `Felhasználónév módosítás: ${oldUsername} -> ${newUsername}`,
      status: 'Felhasználónév módosítás',
      affected_entities: [
        {
          type: 'modify_username',
          new_value: newUsername,
          old_value: oldUsername
        }
      ]
    };

    this.interactionsService.createUserInteraction(interactionData).subscribe({
      next: (response: any) => {
        console.log('Felhasználónév változtatás rögzítve az interactions táblába');
      },
      error: (error: any) => {
        console.error('Hiba az interactions bejegyzés rögzítésekor:', error);
      }
    });
  }
}
